<template>
  <div
    class="tml-image-zoom is-click-to-zoom"
    :class="{
      'is-zoomed': zoomMode,
    }"
    @mousemove="move"
    @mouseup="event => toggleZoom(event)"
    @wheel="event => handleScroll(event)"
    @mouseleave="unzoom"
  >
    <div ref="normal">
      <slot name="default">
        <tml-image
          class="h-full normal"
          :src="imgNormal"
          :alt="alt"
          :style="imgNormalStyle"
          :lazy-load="lazy"
          :src-set="[]"
        />
      </slot>
    </div>
    <div ref="zoom">
      <slot name="zoom">
        <tml-image
          class="zoom click-to-zoom"
          max-width="initial"
          :src="imgZoom || imgNormal"
          :alt="alt"
          :style="imgZoomStyle"
          :lazy-load="lazy"
          :src-set="[]"
          @loaded="onImageLoaded"
        />
      </slot>
    </div>
  </div>
</template>

<script>
import {splitTests} from '@teemill/common/classes';
import TmlImage from '../images/TmlImage.vue';

function pageOffset(el) {
  // -> {x: number, y: number}
  // get the left and top offset of a dom block element
  const rect = el.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  return {
    y: rect.top + scrollTop,
    x: rect.left + scrollLeft,
  };
}

export default {
  components: {TmlImage},

  props: {
    imgNormal: String,
    imgZoom: String,
    disabled: Boolean,
    alt: String,
    lazy: Boolean,
  },

  data: () => ({
    hasMoved: false,

    zoomMode: false,

    imgNormalOpacity: 1,
    imgZoomOpacity: 0,

    imgZoomLeft: 0,
    imgZoomTop: 0,

    imgNaturalWidth: 0,
    imgNaturalHeight: 0,

    hasLoaded: false,
    zoomScale: 1,
    minZoom: 1,
    maxZoom: 2,
  }),

  computed: {
    imgNormalStyle() {
      return {
        opacity: this.imgNormalOpacity,
      };
    },

    imgZoomStyle() {
      return {
        opacity: this.imgZoomOpacity,
        left: this.imgZoomLeft,
        top: this.imgZoomTop,
        width: `${this.imgNaturalWidth * this.zoomScale}px`,
        height: `${this.imgNaturalHeight * this.zoomScale}px`,
      };
    },
  },

  methods: {
    onImageLoaded(e) {
      this.imgNaturalWidth = e.naturalWidth;
      this.imgNaturalHeight = e.naturalHeight;
    },

    toggleZoom(event) {
      if (this.zoomMode) {
        this.unzoom();
      } else {
        this.zoom();
        this.move(event);
      }
    },

    zoom() {
      if (this.disabled) {
        return;
      }

      this.hasMoved = false;
      this.zoomMode = true;
      this.imgZoomOpacity = 1;
      this.imgNormalOpacity = 0;
    },

    unzoom() {
      if (this.$refs.zoom && this.$refs.normal) {
        if (this.disabled) {
          return;
        }

        this.imgZoomOpacity = 0;
        this.imgNormalOpacity = 1;
        this.hasMoved = false;
        this.zoomMode = false;
        this.zoomScale = 1;
      }
    },

    move(event) {
      if (this.$refs.zoom && this.$refs.normal) {
        if (this.disabled || !this.zoomMode) {
          return;
        }

        if (!this.hasMoved) {
          this.hasMoved = true;
          this.imgZoomOpacity = 1;
          this.imgNormalOpacity = 0;
        }

        const offset = pageOffset(this.$el);
        let normal = this.$refs.normal;

        if (normal.$el) {
          normal = normal.$el;
        }

        const relativeX = event.clientX - offset.x + window.pageXOffset;
        const relativeY = event.clientY - offset.y + window.pageYOffset;

        const normalFactorX = relativeX / normal.offsetWidth;
        const normalFactorY = relativeY / normal.offsetHeight;

        const zoomedWidth = this.imgNaturalWidth * this.zoomScale;
        const zoomedHeight = this.imgNaturalHeight * this.zoomScale;

        const x = normalFactorX * (zoomedWidth - normal.offsetWidth);
        const y = normalFactorY * (zoomedHeight - normal.offsetHeight);

        this.imgZoomLeft = `${-x}px`;
        this.imgZoomTop = `${-y}px`;
      }
    },
    handleScroll(event) {
      if (this.disabled || !this.zoomMode) return;

      event.preventDefault();

      const zoomFactor = 0.1;
      const delta = event.deltaY > 0 ? -zoomFactor : zoomFactor;

      const rect = this.$el.getBoundingClientRect();
      const cursorX = event.clientX - rect.left;
      const cursorY = event.clientY - rect.top;

      const xRatio = cursorX / rect.width;
      const yRatio = cursorY / rect.height;

      const oldZoomScale = this.zoomScale;
      const newZoomScale = Math.max(
        this.minZoom,
        Math.min(this.maxZoom, this.zoomScale + delta)
      );

      if (newZoomScale === oldZoomScale) {
        return;
      }
      this.zoomScale = newZoomScale;

      const widthDiff =
        this.imgNaturalWidth * newZoomScale -
        this.imgNaturalWidth * oldZoomScale;

      const heightDiff =
        this.imgNaturalHeight * newZoomScale -
        this.imgNaturalHeight * oldZoomScale;

      const currentLeft = parseFloat(this.imgZoomLeft);
      const currentTop = parseFloat(this.imgZoomTop);

      this.imgZoomLeft = `${currentLeft - widthDiff * xRatio}px`;
      this.imgZoomTop = `${currentTop - heightDiff * yRatio}px`;
    },
  },
};
</script>

<style scoped lang="scss">
.tml-image-zoom {
  position: relative;
  overflow: hidden;

  &.is-click-to-zoom {
    &:not(.is-zoomed) {
      cursor: zoom-in;
    }
    &.is-zoomed {
      cursor: zoom-out;
    }
  }
  .normal {
    width: 100%;
  }

  .zoom {
    position: absolute;
    opacity: 0;
    transform-origin: center;
    transition: transform 0.1s ease-out;
    will-change: transform, left, top;
  }
}
</style>
